import { ComponentProps } from 'react';
import * as React from 'react';
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Text, colors, spacing, usePressable } from '@pletly/ui';
import { GroupButton } from '../GroupButton';
import { CommunityMembersInfo } from './CommunityMembersInfo';

interface Props {
	name: string;
	description?: string | null;
	members: ComponentProps<typeof CommunityMembersInfo>['users'];
	onPress?: () => void;
	ctaButton?: React.ReactNode;
	onJoinPress?: () => void;
	onCancelPress?: () => void;
	style?: StyleProp<ViewStyle>;
}

export const Group = ({
	name,
	description,
	members = [],
	ctaButton,
	onJoinPress,
	onCancelPress,
	onPress,
	style,
}: Props) => {
	const { t } = useTranslation();
	const { isHovered, onHoverIn, onHoverOut } = usePressable();

	return (
		<Pressable
			style={[styles.root, isHovered && styles.hovered, style]}
			onHoverIn={onHoverIn}
			onHoverOut={onHoverOut}
			onPress={onPress}
		>
			<Text category="h4">{name}</Text>

			{description && (
				<Text category="p1" numberOfLines={3}>
					{description}
				</Text>
			)}

			<CommunityMembersInfo users={members} />

			<View style={styles.actions}>
				{ctaButton}

				{onJoinPress && <GroupButton onPress={onJoinPress}>{t('community.join')}</GroupButton>}

				{onCancelPress && (
					<GroupButton variant="outline" onPress={onCancelPress}>
						{t('common.cancel')}
					</GroupButton>
				)}
			</View>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	root: {
		padding: spacing(3),
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignSelf: 'stretch',
		alignItems: 'flex-start',
		gap: spacing(2),
		borderRadius: spacing(2),
		borderWidth: 1,
		borderColor: colors.neutral400,
		flex: 1,
		height: 260,
		minWidth: 252,
		maxWidth: 348,
	},
	hovered: {
		backgroundColor: colors.white,
	},
	actions: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: spacing(2),
	},
});
